
import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import UsersTable from '@/components/masterlicense/users/UsersTable';
import { Users, UserPlus, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMasterUsers } from '@/hooks/useMasterUsers';
import InviteUserDialog from '@/components/masterlicense/users/InviteUserDialog';
import RoleManagement from '@/components/masterlicense/roles/RoleManagement';

const UserManagementTabs = () => {
  const { 
    users, 
    isLoading, 
    showInviteDialog, 
    setShowInviteDialog,
    inviteUser,
    removeUser,
    updateUser,
    loadUsers
  } = useMasterUsers();
  const [activeTab, setActiveTab] = useState('users');

  // Load users when component mounts or after updates
  useEffect(() => {
    loadUsers();
  }, []);

  const handleRemoveUser = async (userId: string): Promise<void> => {
    try {
      await removeUser(userId);
      // Force reload users after removing a user
      await loadUsers();
    } catch (error) {
      console.error("Error removing user:", error);
    }
  };

  const handleInviteUser = async (data: { email: string; role: string; name: string }): Promise<void> => {
    try {
      await inviteUser(data);
      // Force reload users after inviting a user
      await loadUsers();
    } catch (error) {
      console.error("Error inviting user:", error);
    }
  };

  const handleRoleUpdate = async (userId: string, newRole: string): Promise<void> => {
    try {
      await updateUser(userId, { role: newRole as any });
      // Force reload users after updating a role
      await loadUsers();
    } catch (error) {
      console.error("Error updating role:", error);
    }
  };

  const handleStatusUpdate = async (userId: string, newStatus: 'active' | 'suspended'): Promise<void> => {
    try {
      await updateUser(userId, { status: newStatus });
      // Force reload users after updating status
      await loadUsers();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleResendInvitation = async (userId: string): Promise<void> => {
    try {
      // Implement resend invitation logic here if needed
      console.log("Resending invitation to user:", userId);
      // Force reload users after resending invitation
      await loadUsers();
    } catch (error) {
      console.error("Error resending invitation:", error);
    }
  };

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
      <div className="flex justify-between items-center">
        <TabsList>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="roles" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Roles & Permissions
          </TabsTrigger>
        </TabsList>

        {activeTab === 'users' && (
          <Button onClick={() => setShowInviteDialog(true)} className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Invite User
          </Button>
        )}
      </div>

      <TabsContent value="users">
        <UsersTable 
          users={users} 
          isLoading={isLoading} 
          onRemove={handleRemoveUser}
          onRoleUpdate={handleRoleUpdate}
          onStatusUpdate={handleStatusUpdate}
          onResendInvitation={handleResendInvitation}
        />
      </TabsContent>

      <TabsContent value="roles">
        <RoleManagement />
      </TabsContent>

      <InviteUserDialog
        open={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        onInvite={handleInviteUser}
      />
    </Tabs>
  );
};

export default UserManagementTabs;
